import React, { useEffect, useRef, useState } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';

import { PropertyResponse } from '~/services/AccommodationService';

import AccommodationPreviewLink from './AccommodationPreviewLink';

interface Props {
  property: PropertyResponse;
}

export default function AccommodationLinksPanel({ property }: Props) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => handleOpenChange(false), 600);
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!timeoutRef.current && open) {
      timeoutRef.current = setTimeout(() => handleOpenChange(false), 2500); // allow extra time on first open to avoid flickering
    }
    if (timeoutRef.current && !open) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <Button variant="outlined" size="small" color="primary" onClick={() => handleOpenChange(!open)}>
        Preview
      </Button>
      <Stack
        gap={2}
        sx={{
          position: 'absolute',
          top: '3rem',
          right: 0,
          zIndex: 1000,
          padding: '1.2rem',
          minWidth: '30rem',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0px 5px 3px 1px rgba(0, 0, 0, 0.3)',
          display: open ? 'flex' : 'none' /* render once so fetch data once for links */,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Typography variant="h6">Links</Typography>
        {property.connectedOffers && property.connectedOffers.length > 0 ? (
          property.connectedOffers
            .sort((a, b) => {
              if (a.isActive && !b.isActive) {
                return -1;
              }
              if (!a.isActive && b.isActive) {
                return 1;
              }
              return 0;
            })
            .map((offer, i) => (
              <AccommodationPreviewLink
                key={offer.ids.id || offer.idSalesforceExternal || 'link-index-' + i}
                type={offer.offerType}
                name={offer.name}
                ids={offer.ids}
                status={offer.isActive ? 'active' : 'inactive'}
              />
            ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No connected offers
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
